import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useRealtimeDrivers } from 'Core/hooks';
import moment from 'moment';

//Components
import {
    SidePanel,
    SidePanelBody,
    SidePanelFooter,
    Button,
    Select,
    TimeFrame,
    useTimeFrameComponent,
    Form,
    Checkbox
} from '@gpstrackit/gtc-react-controls';
import { Grid } from '@mui/material';

// Actions
import { catalogLoad } from 'Redux/actions'
import {
    getDriverPerformanceEventSearchStatuses,
    getDriverPerformanceHasVideoEvents,
    openModalAdvancedFilters,
    reset
} from './redux/slice';

//Helpers
import { getInitialRange } from '../../../../.globals/helpers'

// Styles
import './index.scss';
import TagsManage from "Modules/.common/components/TagsManage";
const AdvancedFilters = ({ tableRef, formData, setFormData, initialState, applyChanges, hasFilters, savePreferences, loading }) => {

    const dispatch = useDispatch()
    const { messages, locale } = useIntl()

    // Local state for temporary filter values (only applied on "Apply" button)
    const [tempFilters, setTempFilters] = useState({});
    const [entitySelected, setEntitySelected] = useState([]);
    const [optionEventTypes, setOptionsEventTypes] = useState([]);

    // Redux States
    const { open, loadingEvents, responseListEventsVideo, loadingEventsVideo, loadingStatuses, statuses } = useSelector((state) => state.driverPerformanceEventsTabAdvancedFiltersRedux);
    const dateformat = useSelector(state => state.securityRedux.user.dateformat);
    const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });

    const timeFrameOptions = [
        { id: "today", label: messages['date_today'] },
        { id: "last7days", label: messages['date_last_7days'] },
        { id: "last15days", label: messages['date_last_15days'] },
        { id: "last30Days", label: messages['date_last_30days'] },
        { id: "custom", label: messages['date_custom'] }
    ];

    const timeFrame = useTimeFrameComponent(timeFrameOptions, "last7days")

    //Form refs
    const formRef = Form.useForm();

    // Callback to handle changes in TagsManage
    const onChangeEntityTagPicker = useCallback(
        (items, entity) => {
            let newItems = [];

            if (items.length) {
                newItems = items.filter((n) => n.id != -1);
            } else {
                newItems = [];
            }

            // Separate tags and drivers according to the "name" field
            const tags = newItems.filter(item => item.name === "Tag");
            const drivers = newItems.filter(item => item.name === "Driver");

            // Update tempFilters with separated tags and drivers
            setTempFilters({
                ...tempFilters,
                tags: tags.map(tag => ({ id: tag.id, label: tag.label })),
                drivers: drivers.map(driver => ({
                    driverId: driver.id,
                    name: driver.label
                }))
            });

            setEntitySelected(newItems);
        },
        [tempFilters]
    );

    // Definir entidades disponibles
    const entities = useMemo(
        () => [
            { id: "Tag", label: messages["driver_performance_events_filters_tags"] },
            { id: "Driver", label: messages["clipRequestAdvancedFilters_drivers"] },
        ],
        [messages]
    );

    // Sync tempFilters when formData changes or modal opens
    useEffect(() => {
        if (open && formData) {
            // Initialize tempFilters with current formData values when modal opens
            setTempFilters({ ...formData });

            // Synchronize entitySelected with formData values
            const selectedEntities = [];

            // Add tags to entitySelected
            if (formData?.tags && formData.tags.length > 0) {
                formData.tags.forEach(tag => {
                    selectedEntities.push({
                        id: tag.id,
                        label: tag.label,
                        name: "Tag",
                        parentId: tag.parentId || null
                    });
                });
            }

            // Add drivers to entitySelected
            if (formData?.drivers && formData.drivers.length > 0) {
                formData.drivers.forEach(driver => {

                    let currentTag = driver.tags && driver.tags.length > 0 ? driver.tags[0].parentId : null

                    selectedEntities.push({
                        id: driver.driverId,
                        label: driver.name,
                        name: "Driver",
                        parentId: currentTag || null
                    });
                });
            }

            setEntitySelected(selectedEntities);

            if (formData?.timeFrame && formData?.rangeDate?.start && formData?.rangeDate?.end) {
                let currentTime = timeFrameOptions.find((item) => item?.id === formData.timeFrame)
                timeFrame.onChangeSelect(currentTime, formData.rangeDate.start, formData.rangeDate.end)
            }
            formRef?.setValues(formData);
        }
    }, [open, formData])

    useEffect(() => {
        if (open && !responseListEventsVideo?.length) {
            dispatch(getDriverPerformanceHasVideoEvents({}))
        }
    }, [open])

    useEffect(() => {
        if (responseListEventsVideo?.length) {
            // Check if "Unassigned Driver" already exists in the original list
            const hasUnassignedDriver = responseListEventsVideo.some(
                event => event?.eventName === 'Unassigned Driver'
            );

            // Add "Unassigned Driver" to the original list if it doesn't exist
            const eventsToProcess = hasUnassignedDriver 
                ? responseListEventsVideo 
                : [...responseListEventsVideo, {
                    eventName: "Unassigned Driver",
                    rawEvents: ["CameraEvent.UNASSIGNED_DRIVER"]
                }];

            const transformedEvents = eventsToProcess.map((event) => {
                let key =
                    event?.eventName.replace(/\s+/g, "").charAt(0).toLowerCase() +
                    event?.eventName.replace(/\s+/g, "").slice(1);

                let label = messages[`videoMainView_${key}`]
                    ? messages[`videoMainView_${key}`]
                    : event?.eventName;
                return { key, label, events: [event?.eventName] };
            }).filter((event) => {
                const excludedKeys = ['potentialIncidentHigh', 'potentialIncidentLow', 'potentialIncidentMedium'];
                return !excludedKeys.includes(event.key);
            });

            const orderer = transformedEvents?.sort((a, b) =>
                a.label.localeCompare(b.label)
            );
            setOptionsEventTypes(orderer);
        }
    }, [responseListEventsVideo, locale, messages]);

    useEffect(() => {
        if (open) {
            const { startDate, endDate } = timeFrame;
            let date = getInitialRange(timeFrame?.option?.id, startDate, endDate);

            let currentDateRange = {
                start: date?.start,
                end: date?.end
            }

            setTempFilters({
                ...tempFilters,
                rangeDate: currentDateRange,
                timeFrame: timeFrame?.option?.id
            });
        }
    }, [timeFrame?.startDate, timeFrame?.endDate]);

    useEffect(() => {
        return () => {
            dispatch(reset())
        }
    }, [])

    const clearFilters = () => {
        const conditions = [{
            range: {
                fields: ["rangeDate"],
                GTE: moment.utc().startOf('month').toDate(),
                LTE: moment.utc().subtract(1, 'days').endOf('day').toDate()
            },
            page: 1,
            openSearch: formData?.openSearch || ""
        }];

        //Refresh table with initial conditions
        tableRef.refresh({ _conditions: conditions });

        const defaultPreferences = ["rangeDate", "timeFrame", "events", "tags", "drivers", "status", 'driverDisputed']
            .map(key => ({ key, value: null }));

        //Preferences
        savePreferences(defaultPreferences)

        const clearedData = {
            ...initialState,
            openSearch: formData?.openSearch || ""
        };

        setFormData(clearedData);
        setTempFilters(clearedData);

        // Clear entitySelected
        setEntitySelected([]);

        // Reset TimeFrame component
        formRef?.resetValues();
        timeFrame.reset();
    }

    useEffect(() => {
        if (open && drivers.length === 0) {
            dispatch(catalogLoad(['drivers']))
        }
        dispatch(getDriverPerformanceEventSearchStatuses({}))
    }, [open])

    return (
        <SidePanel
            isOpen={open}
            onToggle={() => dispatch(openModalAdvancedFilters({ open: !open }))}
            style={{ zIndex: 2, width: '380px', transform: 'translateX(380px)' }}
            classSidePanel={hasFilters() ? "dp-events-tab-advanced-filters-available" : ""}
        >
            <SidePanelBody>
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <h2 style={{ margin: 0 }}>
                            {messages['workOrder_tab_advancedFilters_title']}
                        </h2>
                        <span
                            className='btn-link'
                            onClick={() => {
                                if (!loading) {
                                    clearFilters()
                                }
                            }}
                            style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
                        >
                            {messages['workOrder_tab_advancedFilters_clearAll']}
                        </span>
                    </Grid>
                </Grid>
                <div style={{ width: 330, marginTop: 10 }}>
                    <Form
                        ref={formRef.ref}
                        loading={loadingEvents || loading}
                        onValidSubmit={() => {
                            // Reset page and apply temporary filters
                            const filtersWithPage = { ...tempFilters, page: 1, excludedNoIssueEvents: false };

                            // Apply temporary filters to formData
                            setFormData(filtersWithPage);

                            // Save all preferences when applying filters
                            savePreferences([
                                {
                                    key: "rangeDate",
                                    value: tempFilters.rangeDate
                                },
                                {
                                    key: "timeFrame",
                                    value: tempFilters.timeFrame
                                },
                                {
                                    key: "events",
                                    value: tempFilters.events
                                },
                                {
                                    key: "drivers",
                                    value: tempFilters.drivers
                                },
                                {
                                    key: "tags",
                                    value: tempFilters.tags
                                },
                                {
                                    key: "status",
                                    value: tempFilters.status
                                },
                                {
                                    key: "driverDisputed",
                                    value: tempFilters.driverDisputed || false
                                }
                            ]);
                            // Pass tempFilters directly to avoid issues with asynchronous state
                            applyChanges(filtersWithPage);
                        }}
                        onChangeValues={() => { }}
                        className="dp-events-tab-advanced-filters"
                    >
                        <Form.Item
                            name="timeFrame"
                            label={messages['driver_performance_events_filters_timeFrame']}
                            style={{ marginBottom: 10 }}
                        >
                            <div style={{ width: 330, marginTop: -10 }} className="dp-events-tab-advanced-filters-timeframe">
                                <TimeFrame
                                    isRequired
                                    filterSwitch={false}
                                    {...timeFrame}
                                    maxDate={moment()}
                                    dateFormat={dateformat || 'Y/m/d'}
                                    withTime={false}
                                    openDateRangePickerModalAutomatically={false}
                                    startDate={moment(tempFilters?.rangeDate?.start)}
                                    endDate={moment(tempFilters?.rangeDate?.end)}
                                    minDate={moment().subtract(90, "d")}
                                />
                            </div>
                        </Form.Item>
                        <div style={{ marginBottom: 10 }}>
                            <Select
                                name="events"
                                textField="label"
                                label={messages['driver_performance_events_filters_events']}
                                valueField="key"
                                placeholder={messages['driver_performance_events_filters_events']}
                                isMulti={true}
                                options={optionEventTypes}
                                isDisabled={false}
                                isLoading={loadingEventsVideo}
                                isClearable={true}
                                isSearchable={true}
                                showPillsOutside={true}
                                value={tempFilters?.events || []}
                                onChange={(value) => {
                                    setTempFilters({ ...tempFilters, events: value })
                                }}
                            />
                            {
                                optionEventTypes?.length === 0 && !loadingEventsVideo ? (
                                    <div>
                                        <small className="text-danger">{messages['driver_performance_events_filters_events_helpText']}</small>
                                    </div>
                                ) : <></>
                            }
                        </div>
                        <div style={{ marginBottom: 10 }}>
                            <Select
                                name="status"
                                label={messages['driver_performance_events_filters_status']}
                                placeholder={messages['driver_performance_events_filters_status']}
                                id="status"
                                options={(statuses || []).map((item) => ({ ...item, label: messages[`driver_performance_${item?.entityId}`] || item?.label })).filter(status => ["verified", "highlightcoaching", "noissue"].includes(status?.entityId)).sort((a, b) => a?.label.localeCompare(b?.label))}
                                textField="label"
                                valueField="entityId"
                                onChange={(value) => {
                                    setTempFilters({ ...tempFilters, status: value })
                                }}
                                value={tempFilters?.status || []}
                                isLoading={loadingStatuses}
                                isClearable={true}
                                isSearchable={true}
                                showPillsOutside={true}
                                isMulti={true}
                            />
                        </div>
                        <div style={{ marginBottom: 5, display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                                id="driverDisputed"
                                name="driverDisputed"
                                checked={tempFilters?.driverDisputed || false}
                                onChange={(value) => {
                                    setTempFilters({ ...tempFilters, driverDisputed: value })
                                }}
                            />
                            <label className="label-gtc-rc">{messages['driver_performance_events_filters_driverDisputed']}</label>
                        </div>
                        <div>
                            <div className="events-tab-tags" style={{ width: '100%', overflow: "auto", marginTop: 5 }}>
                                <div className="input-gtc-rc-label"><label className="label-gtc-rc">{messages['driver_performance_cases_filters_tags']}</label></div>
                                <TagsManage
                                    entities={entities}
                                    onChange={onChangeEntityTagPicker}
                                    hideTagsWithoutEntities={true}
                                    mode="list"
                                    height={400}
                                    value={entitySelected}
                                    noPills={true}
                                    entitySelected={{ id: "Driver", label: messages["clipRequestAdvancedFilters_drivers"] }}
                                    searchByTag={true}
                                />
                            </div>
                        </div>
                    </Form>
                </div>
            </SidePanelBody>
            <SidePanelFooter>
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                    <Button
                        isDisabled={loadingEvents || loading}
                        style={{ marginRight: 8 }}
                        onClick={() => dispatch(reset({ open: false }))}
                        variant="secondary"
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loadingEvents || loading || (!timeFrame?.startDate || !timeFrame?.endDate)}
                        onClick={() => formRef?.submit()}
                        variant="primary"
                    >
                        {messages["apply"]}
                    </Button>
                </div>
            </SidePanelFooter>
        </SidePanel>
    )
}

export default AdvancedFilters